import { WEEK_MS } from "./freshness.js";
import { epley, isE1rmRepCount, roundToHalfKg } from "./pr.js";
import { effectiveRir } from "./effortScale.js";
import type { Workout, WorkoutSet } from "./schemas/workout.js";
import type { MaxSource } from "./schemas/max.js";

// Bidirectional MAX — the LOWER half. The RAISE half lives in maxSuggest.ts
// (suggestMaxUpdate / suggestMaxUpdatesForWorkout); this module is its
// mirror. It proposes pulling a stored MAX *down* when a genuine
// maximal-effort set proves the ceiling has dropped (detraining, injury,
// age). Spec: next/measurement-contract.md «Il MAX dinamico: il come», Antenore 2026-06-17. Today the stored
// MAX is a one-way ratchet — it rises on a new best but nothing ever
// pulls it down, so after a layoff it sits stale-high and every percent
// prescription comes out too heavy. This closes the loop.
//
// The asymmetry is the whole point:
//   - RAISE: ANY set whose e1RM exceeds the MAX is valid evidence — if
//     you lifted w×r your 1RM is at least the e1RM, regardless of effort.
//   - LOWER: ONLY a true ceiling attempt (RIR 0 or fail) counts. A
//     submaximal set that computes low proves nothing — you just didn't
//     push. ⇒ a deliberately-light block (RIR 2-3, e.g. the hernia
//     "Light" program) almost never qualifies, so the system self-parks
//     in light phases and re-evaluates only when the athlete genuinely
//     grinds a set to failure on return to heavy work. Exactly desired:
//     it never punishes you for training light on purpose.
//
// Never auto-writes — the athlete is the final judge (the end-of-session
// confirm prompt, see useMaxBumpCandidates). On confirm the lowered value
// is stored with source:"tested" (it came from a real failed/ceiling
// attempt), which also protects it from a later seed-from-history
// re-derive bumping it back up (a re-derive preserves manual/tested and
// overwrites only auto-origin seeds).

// Day-to-day within-subject 1RM swing is a few % (literature ~3-5%), so a
// 5% band below the stored MAX avoids false alarms from normal noise.
export const MAX_REGRESSION_DELTA = 0.05;

// A full training cycle must pass before a downward revision is plausible
// (most blocks are 6 weeks, Antenore 2026-06-17) — anti-thrash.
export const MAX_REGRESSION_STALE_WEEKS = 6;

// How many SEPARATE sessions must contradict a MAX before its calendar
// immunity is bypassed (Antenore 2026-09-07, `MX-11`).
//
// ⭐ Derived from his data, never chosen ([[feedback_derive_thresholds_from_data_not_constants]]).
// Distinct training days per exercise over the four weeks to 2026-09-07,
// counted in DDB: barbell-row 6, bench-press 8, back-squat 4, overhead-press
// 3 — and lat-pulldown, a lift he touched once, 1. Three sits UNDER the count
// of every lift he actually trains (so the rule reaches them) and ABOVE the
// lift he visited once (so a single visit can never pull a MAX down).
//
// ⛔ NOT a second staleness number. It counts SESSIONS, not weeks, and it is
// the evidence half of the same question `MAX_REGRESSION_STALE_WEEKS` answers
// with a calendar.
export const MAX_REGRESSION_CONTRADICTING_SESSIONS = 3;

// Recent-ceiling corroboration window (Antenore 2026-06-26). One saved
// session is a single data point: a light/off-day RIR0 back-off set can
// compute below the noise band and falsely pull the MAX down even though
// a session a few days earlier proved a higher ceiling. We therefore look
// back this far for the best qualifying e1RM and refuse to lower when a
// recent session was at least as strong — a real decline is sustained,
// not one light day. 4 weeks ≈ a mesocycle of corroboration.
export const MAX_REGRESSION_RECENT_WEEKS = 4;

// The AGE half of "may this MAX propose a load a human will lift". Its twin is
// `isMaxTrustedForProposal` (schemas/max.ts), which answers the PEDIGREE half,
// and the two are asked together at every callsite.
//
// ⛔ It lives HERE and not beside its twin, and the reason is mechanical rather
// than conceptual: `scripts/api-contract-hash.sh` hashes every file under
// `schemas/` WHOLE, because those files ARE the wire contract with the API. A
// client-side staleness policy is not something the server validates, so
// putting it there demanded a prod deploy for a change the API cannot see —
// the release gate went red on 2026-09-02 for exactly that, and a gate that
// cries wolf is one people learn to wave through
// ([[feedback_a_gate_that_always_fires_stops_being_a_gate]]). This file is also
// where the OTHER "how old is a MAX" question already lived, so the two ages
// now sit together and cannot drift apart unnoticed.
//
// ⛔ NOT `MAX_REGRESSION_STALE_WEEKS` (maxRegression.ts, 6). That one is
// anti-thrash on LOWERING a stored MAX — "a full training block must pass
// before a downward revision is plausible" — and aliasing the two would make
// a change to either silently move the other. Different question, different
// number, on purpose.
//
// ⭐ 8 is ANTENORE'S, with his reason, and is not derived from his data the
// way `exerciseStaleness`'s 28/14 are (2026-09-02): «il consenso comune è tra
// 8 e 12 settimane... direi per protezionismo se inferiore o uguale a 8
// settimane». Recorded as his call rather than dressed up as a measurement.
export const PROPOSAL_MAX_STALE_WEEKS = 8;

/** Is this MAX too old to LEAD the proposal chain?
 *
 *  A stale MAX is not refused — it is demoted below the athlete's own recent
 *  history (measurement-contract.md §17.5). It still shows in the panel, so
 *  nothing disappears; it just stops being the number that fills the cell.
 *
 *  ⛔ An unknown `setAt` reads as STALE, never as fresh — the same safe
 *  direction `isMaxTrustedForProposal` takes for an unknown source. A caller
 *  that forgets to plumb the date gets the cautious answer, not the confident
 *  one. */
export const isMaxStaleForProposal = (
  setAt: string | undefined,
  nowMs: number,
): boolean => {
  if (setAt === undefined) return true;
  const setAtMs = Date.parse(setAt);
  if (!Number.isFinite(setAtMs)) return true;
  return nowMs - setAtMs > PROPOSAL_MAX_STALE_WEEKS * WEEK_MS;
};


// The stored MAX a regression is measured against: value + when it was
// set. `setAt` drives the staleness gate (the MAX schema's own field —
// no new persisted field needed).
export type CurrentMax = {
  valueKg: number;
  setAt: string;
  /** Where the value came from. Optional because the regression check does
   *  not need it — it is carried for the readers that must decide whether a
   *  MAX may be used to PROPOSE a load (isMaxTrustedForProposal, EQ-1-SWAP-
   *  PCTMAX). Absent means "unknown", which those readers treat as untrusted,
   *  never as trusted. */
  source?: MaxSource;
};

export type MaxRegression = {
  // Proposed lower value (rounded to 0.5 kg) = the strongest qualifying
  // ceiling attempt in the session. Conservative on the way DOWN: lower
  // the MAX only to the best attempt, never below it — a weaker later set
  // is fatigue within the session, not proof of a lower ceiling.
  suggestedKg: number;
  e1RM: number; // the representative (best qualifying) e1RM, for transparency
  currentMaxKg: number;
  weeksSinceMax: number; // floored whole weeks since the MAX was set
  fromSet: { weightKg: number; reps: number };
};

// Is this set a true ceiling attempt eligible to LOWER a MAX? Weighted
// only — epley() needs a real lifted load and the MAX surface is keyed on
// absolute kg (bodyweight progressions surface via isSetPR, never here).
// Maximal effort = RIR 0 OR fail; reps within the Epley cap (12).
//
// Note: a `fail` set is exactly the maximal-effort signal we want for the
// LOWER path — its `reps` are the clean reps completed (AP-0p model: the
// failed rep was already decremented at log time) and `fail` is a pure
// label, so epley(weight, reps) is valid ceiling evidence, the same rule
// computeE1rmKg uses for the RAISE path.
const isCeilingAttempt = (
  s: WorkoutSet,
): s is Extract<WorkoutSet, { type: "weighted" }> => {
  if (s.type !== "weighted") return false;
  // Maximal effort = RIR 0 OR fail, via the shared fail⇒RIR-0 predicate
  // (one definition, shared with the AP-2 hard-set accumulator).
  const maximal = effectiveRir(s) === 0;
  if (!maximal) return false;
  if (!isE1rmRepCount(s.reps)) return false;
  if (s.weight <= 0) return false;
  return true;
};

/** Does the recent window prove this MAX was never corroborated?
 *
 *  TRUE only when the window can actually speak — at least
 *  `MAX_REGRESSION_CONTRADICTING_SESSIONS` separate sessions carrying a
 *  qualifying e1RM for this exercise — and NOT ONE of them reached the stored
 *  MAX's noise band. Several separate days, every one of them below: that is
 *  a MAX resting on a single measurement, not one bad day.
 *
 *  ⛔ Absent evidence reads as CORROBORATED, never as uncorroborated: a caller
 *  that has not plumbed the counts keeps the calendar gate exactly as it was.
 *  ⛔ And this only ever BYPASSES the calendar. Every other gate below still
 *  runs, so a light day still cannot lower anything. */
const isUncorroborated = (
  cur: CurrentMax,
  params: {
    recentBestE1RM?: number;
    recentQualifyingSessions?: number;
  },
): boolean => {
  const sessions = params.recentQualifyingSessions;
  if (sessions === undefined || sessions < MAX_REGRESSION_CONTRADICTING_SESSIONS) {
    return false;
  }
  const best = params.recentBestE1RM;
  if (best === undefined) return false;
  return best < cur.valueKg * (1 - MAX_REGRESSION_DELTA);
};

// Per-exercise regression check over one session's sets. Returns the
// proposed lower MAX, or null when no downward revision is warranted.
// All four gates must hold (see file header). `nowMs` is injected for
// testability.
export const detectMaxRegression = (params: {
  sets: readonly WorkoutSet[];
  currentMax: CurrentMax | null | undefined;
  // Best qualifying e1RM (kg) over the recent corroboration window
  // (MAX_REGRESSION_RECENT_WEEKS), EXCLUDING the just-saved session.
  // Effort-agnostic on this side: a high e1RM proves capacity regardless
  // of how the set felt (mirrors the RAISE path — any set is evidence).
  // When a recent session was at least as strong as today's best ceiling
  // attempt, today is a light/off day, not a sustained decline → no lower.
  // Optional: omitted ⇒ legacy single-session behaviour (no corroboration).
  recentBestE1RM?: number;
  // How many separate sessions inside the same window carried a qualifying
  // e1RM for this exercise (`MX-11`). Read together with `recentBestE1RM`:
  // enough sessions, none of them reaching the MAX ⇒ the MAX was never
  // corroborated and its six-week calendar immunity is bypassed.
  // ⛔ Absent ⇒ the calendar gate stands, unchanged.
  recentQualifyingSessions?: number;
  nowMs: number;
}): MaxRegression | null => {
  const cur = params.currentMax;
  if (cur == null) return null; // nothing to lower (manual seed required first)

  // Staleness gate: a full cycle must have passed since the MAX was set —
  // UNLESS the MAX was never corroborated (`MX-11`, Antenore 2026-09-07:
  // «l'immunità la dà la PROVA, non il calendario»). A MAX minted by a single
  // measurement — his barbell-row 84 kg came from ONE AMRAP on 2026-08-13 —
  // otherwise writes every prescription for six weeks while the athlete's own
  // sessions say it is too high, and nothing can reach it.
  const ageMs = params.nowMs - new Date(cur.setAt).getTime();
  const weeksSinceMax = Math.floor(ageMs / WEEK_MS);
  if (
    ageMs < MAX_REGRESSION_STALE_WEEKS * WEEK_MS &&
    !isUncorroborated(cur, params)
  ) {
    return null;
  }

  // Best (highest e1RM) qualifying ceiling attempt in the session.
  let best: { e1RM: number; weightKg: number; reps: number } | null = null;
  for (const s of params.sets) {
    if (!isCeilingAttempt(s)) continue;
    const e = epley(s.weight, s.reps);
    if (!best || e > best.e1RM) {
      best = { e1RM: e, weightKg: s.weight, reps: s.reps };
    }
  }
  if (!best) return null;

  // Recent-ceiling gate (Antenore 2026-06-26): refuse to lower when a
  // session inside the corroboration window already proved a qualifying
  // e1RM at or above today's strongest ceiling attempt. That earlier
  // session shows the ceiling has NOT actually dropped — today's lighter
  // grind is an off/light day, not a sustained decline. (Without this,
  // a single light-day RIR0 back-off set ratchets the MAX down past a
  // recent stronger session — the incline-press false positive.)
  if (
    params.recentBestE1RM != null &&
    params.recentBestE1RM >= best.e1RM
  ) {
    return null;
  }

  // Below the noise band? Even the strongest ceiling attempt must fall
  // ≥ δ below the stored MAX to count as a real decline.
  if (best.e1RM >= cur.valueKg * (1 - MAX_REGRESSION_DELTA)) return null;

  const rounded = roundToHalfKg(best.e1RM);
  // Guard: rounding mustn't land on/above the current MAX (would be a
  // no-op or an accidental raise).
  if (rounded >= cur.valueKg) return null;

  return {
    suggestedKg: rounded,
    e1RM: best.e1RM,
    currentMaxKg: cur.valueKg,
    weeksSinceMax,
    fromSet: { weightKg: best.weightKg, reps: best.reps },
  };
};

// Whole-workout aggregation: one regression per exercise (if any). Mirror
// of suggestMaxUpdatesForWorkout — used by the post-save UX so the
// athlete sees one prompt per exercise, not one per set.
export const suggestMaxRegressionsForWorkout = (params: {
  workout: Workout;
  currentMaxes: Record<string, CurrentMax | null | undefined>;
  // Per-exercise best qualifying e1RM over the recent corroboration
  // window, EXCLUDING the just-saved workout (see detectMaxRegression).
  // Built from the recent-history cache by the caller; absent slugs =
  // no recent evidence ⇒ legacy single-session behaviour for that lift.
  recentBestE1RM?: Record<string, number>;
  // Per-exercise count of separate qualifying sessions in the SAME window
  // (`MX-11`). Read beside `recentBestE1RM`; absent ⇒ calendar gate unchanged.
  recentSessionCount?: Record<string, number>;
  nowMs: number;
}): Record<string, MaxRegression> => {
  const out: Record<string, MaxRegression> = {};
  for (const entry of params.workout.entries) {
    const exId = entry.exerciseId;
    const reg = detectMaxRegression({
      sets: entry.sets,
      currentMax: params.currentMaxes[exId],
      recentBestE1RM: params.recentBestE1RM?.[exId],
      recentQualifyingSessions: params.recentSessionCount?.[exId],
      nowMs: params.nowMs,
    });
    if (reg) out[exId] = reg;
  }
  return out;
};

// The recent-window evidence per exercise, in ONE walk: the best qualifying
// e1RM and HOW MANY separate sessions produced one. Mirrors the RAISE-side
// evidence rule (computeE1rmKg): any weighted set within the Epley rep cap is
// valid 1RM evidence, INCLUDING a fail (AP-0p model: its `reps` are the clean
// reps done, `fail` is a pure label). Pure — the caller supplies the window
// and `nowMs`.
//
// ⛔ The count is of SESSIONS, not sets: three sets in one workout are one
// day's evidence, and `MX-11` asks how many separate days spoke.
//
// ⚠️ The two numbers come from the SAME walk and the SAME eligibility test on
// purpose. Deriving the count separately would be a second definition of
// "qualifying", and the two could then disagree about whether a session
// counted — the exact trap `entryHistoryE1rm.ts` documents for "best e1RM".
export type RecentExerciseEvidence = {
  bestE1RM: number;
  /** Separate sessions inside the window that carried a qualifying set. */
  sessions: number;
};

export const recentEvidenceByExerciseInWindow = (params: {
  workouts: readonly Workout[];
  nowMs: number;
  windowWeeks?: number;
}): Record<string, RecentExerciseEvidence> => {
  const cutoffMs =
    params.nowMs - (params.windowWeeks ?? MAX_REGRESSION_RECENT_WEEKS) * WEEK_MS;
  const out: Record<string, RecentExerciseEvidence> = {};
  for (const w of params.workouts) {
    const startedMs = new Date(w.startedAt).getTime();
    if (!Number.isFinite(startedMs) || startedMs < cutoffMs) continue;
    // One workout counts ONCE per exercise however many entries or sets it
    // carries for it (A-B-A alternation is still a single day).
    const seenHere = new Set<string>();
    for (const entry of w.entries) {
      for (const s of entry.sets) {
        if (s.type !== "weighted") continue;
        if (!isE1rmRepCount(s.reps)) continue;
        if (s.weight <= 0) continue;
        const e = epley(s.weight, s.reps);
        const prev = out[entry.exerciseId];
        if (prev === undefined) {
          out[entry.exerciseId] = { bestE1RM: e, sessions: 0 };
        } else if (e > prev.bestE1RM) {
          prev.bestE1RM = e;
        }
        seenHere.add(entry.exerciseId);
      }
    }
    for (const slug of seenHere) {
      const rec = out[slug];
      if (rec !== undefined) rec.sessions += 1;
    }
  }
  return out;
};

// Best qualifying e1RM (kg) per exercise over a trailing window — the narrow
// accessor. ⛔ DEFINED as a projection of `recentEvidenceByExerciseInWindow`,
// never as a second walk, so the number that feeds the noise band and the
// number that feeds the session count can never come from two different
// readings of the same window.
//
// ⚠️ It has no production caller since `MX-11` (2026-09-07) moved
// `snapshotRecentEvidence` onto the one-walk function. It stays because
// `apps/mobile/__tests__/entryHistoryE1rmDerivation.test.ts` uses it as the
// RIVAL in the guard that stops `entryHistoryE1rm.ts`'s "best e1RM" from being
// collapsed into it — the two answer different questions (this one applies a
// 4-week cutoff and counts every entry; that one takes the first entry per
// workout and no cutoff). ⛔ Deleting it deletes that guard, which is a
// decision about `CN-BESTE1RM-ORPHAN`, not cleanup.
export const bestE1rmByExerciseInWindow = (params: {
  workouts: readonly Workout[];
  nowMs: number;
  windowWeeks?: number;
}): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [slug, ev] of Object.entries(
    recentEvidenceByExerciseInWindow(params),
  )) {
    out[slug] = ev.bestE1RM;
  }
  return out;
};
